import { normalizeEuro } from '@workspace/utils';

export interface TarifRecapProps {
    formule: {
        nom: string;
        tarif: number;
        tarifOriginal?: number; // Prix avant promo (pour affichage barré)
    };
    options: Array<{
        nom: string;
        tarif: number;
        tarifOriginal?: number; // Prix avant promo (pour affichage barré)
    }>;
    fraisDossier?: number;
    taxeAttentat?: number;
    fractionnement?: 'M' | 'A'; // 'M' = Mensuel (défaut), 'A' = Annuel uniquement
    textes: {
        titre: string;
    };
}

export const TarifRecap = ({ formule, options, fraisDossier = 0, taxeAttentat = 0, fractionnement = 'M', textes }: TarifRecapProps) => {
    const isAnnuel = fractionnement === 'A';
    const periode = isAnnuel ? 'an' : 'mois';

    // En mode annuel, les tarifs passés sont déjà annuels
    // En mode mensuel, on calcule l'annuel à partir du mensuel
    const totalPrincipal = formule.tarif + options.reduce((acc, opt) => acc + opt.tarif, 0);
    const totalSecondaire = isAnnuel
        ? Math.round((totalPrincipal / 12) * 100) / 100 // Calcul du mensuel à partir de l'annuel
        : Math.round(totalPrincipal * 12 * 100) / 100;  // Calcul de l'annuel à partir du mensuel
    const fraisPeriode = isAnnuel ? fraisDossier : fraisDossier / 12;
    const taxePeriode = isAnnuel ? taxeAttentat : taxeAttentat / 12;

    // Calculer le total original si des prix promos sont présents
    const hasPromo = Boolean(formule.tarifOriginal || options.some(opt => opt.tarifOriginal));
    const totalPrincipalOriginal = hasPromo
        ? (formule.tarifOriginal || formule.tarif) + options.reduce((acc, opt) => acc + (opt.tarifOriginal || opt.tarif), 0)
        : undefined;
    const totalSecondaireOriginal = totalPrincipalOriginal
        ? (isAnnuel ? Math.round((totalPrincipalOriginal / 12) * 100) / 100 : Math.round(totalPrincipalOriginal * 12 * 100) / 100)
        : undefined;

    return (
        <div className="w-full mt-6">
            <div className="rounded-lg bg-card p-4 sm:p-6">
                <h3 className="text-base sm:text-lg text-foreground mb-4">{textes.titre}</h3>

                <div>
                    <div className="p-3 sm:p-4 border-b border-border">
                        <div className="space-y-2 text-xs">
                            <div className="flex justify-between items-center">
                                <span className="text-muted-foreground">{formule.nom}</span>
                                <div className="text-right">
                                    <span className="font-medium">{normalizeEuro(formule.tarif)}/{periode}</span>
                                    {formule.tarifOriginal && formule.tarifOriginal > formule.tarif && (
                                        <div className="text-[10px] line-through opacity-60">
                                            {normalizeEuro(formule.tarifOriginal)}/{periode}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {options.map((option) => (
                                <div
                                    key={option.nom}
                                    className="flex justify-between items-center"
                                >
                                    <span className="text-muted-foreground">{option.nom}</span>
                                    <div className="text-right">
                                        <span className="font-medium">+{normalizeEuro(option.tarif)}/{periode}</span>
                                        {option.tarifOriginal && option.tarifOriginal > option.tarif && (
                                            <div className="text-[10px] line-through opacity-60">
                                                +{normalizeEuro(option.tarifOriginal)}/{periode}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-card p-3 sm:p-4">
                        <div className="flex justify-between items-center">
                            <span className="text-foreground font-medium">Total</span>
                            <div className="text-right">
                                <div className="font-semibold text-primary text-base">{normalizeEuro(totalPrincipal)}/{periode}*</div>
                                {hasPromo && totalPrincipalOriginal && totalPrincipalOriginal > totalPrincipal && (
                                    <div className="text-xs line-through opacity-60 text-muted-foreground">
                                        {normalizeEuro(totalPrincipalOriginal)}/{periode}*
                                    </div>
                                )}
                                {!isAnnuel && (
                                    <>
                                        <small className="text-primary text-xs">soit {normalizeEuro(totalSecondaire)}/an*</small>
                                        {hasPromo && totalSecondaireOriginal && totalSecondaireOriginal > totalSecondaire && (
                                            <div className="text-[10px] line-through opacity-60 text-muted-foreground">
                                                soit {normalizeEuro(totalSecondaireOriginal)}/an*
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        <p className="text-[10px] lg:text-xs text-neutral-500 mt-2">
                            *Prix TTC
                            {(fraisPeriode > 0 || taxePeriode > 0) && (
                                <>
                                    {fraisPeriode > 0 && ` incluant ${normalizeEuro(fraisPeriode)} de frais`}
                                    {taxePeriode > 0 && `${fraisPeriode > 0 ? ' et ' : ' incluant '}${normalizeEuro(taxePeriode)} de taxe attentat`}
                                </>
                            )}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};